/**
 * ElevationHistogramWeb — altitude-distribution histogram for a segment on the
 * public trip page. Companion to `ElevationProfileWeb`: same styling, same
 * `coordinates` input ({ lat, lng, alt, t }).
 *
 * Computes km travelled inside each altitude bin (auto bin-size based on
 * range) and renders vertical bars with hover tooltip. No external chart lib.
 */
import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Mountain } from "lucide-react";

const EARTH_R_KM = 6371;
const toRad = (v) => (v * Math.PI) / 180;

function haversine(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Pick a sensible bin size (metres) from the altitude span. */
function chooseBinSize(range) {
  if (range <= 0) return 50;
  if (range < 150) return 10;
  if (range < 400) return 25;
  if (range < 800) return 50;
  if (range < 1500) return 100;
  return 200;
}

function buildHistogram(points) {
  if (!points || points.length < 2) {
    return { bins: [], binSize: 0, totalKm: 0 };
  }
  const alts = points
    .map((p) => (Number.isFinite(p.alt) ? p.alt : null))
    .filter((v) => v !== null);
  if (alts.length < 2) return { bins: [], binSize: 0, totalKm: 0 };

  const minAlt = Math.min(...alts);
  const maxAlt = Math.max(...alts);
  const range = maxAlt - minAlt;
  const binSize = chooseBinSize(range);

  // Snap lower bound to a multiple of binSize so labels are clean.
  const lower = Math.floor(minAlt / binSize) * binSize;
  const upper = Math.ceil(maxAlt / binSize) * binSize;
  const nBins = Math.max(1, Math.round((upper - lower) / binSize));

  const bins = Array.from({ length: nBins }, (_, i) => ({
    low: lower + i * binSize,
    high: lower + (i + 1) * binSize,
    km: 0,
  }));

  // b99.10.72 (2026-05-23) — Prefer upstream `distance_km` when all
  // points carry it (e.g. backend `/api/routes/elevation` already
  // scales by the Google Routes road distance). Avoids the same
  // ~4 % underestimate that affected the profile chart.
  const useUpstreamKm = points.every(
    (p) => typeof p.distance_km === "number" && Number.isFinite(p.distance_km),
  );

  let totalKm = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (!Number.isFinite(a.alt) || !Number.isFinite(b.alt)) continue;
    const segKm = useUpstreamKm
      ? Math.max(0, b.distance_km - a.distance_km)
      : haversine(a, b);
    if (!Number.isFinite(segKm) || segKm <= 0) continue;
    const avgAlt = (a.alt + b.alt) / 2;
    let idx = Math.floor((avgAlt - lower) / binSize);
    if (idx < 0) idx = 0;
    if (idx >= nBins) idx = nBins - 1;
    bins[idx].km += segKm;
    totalKm += segKm;
  }

  return { bins, binSize, totalKm };
}

/**
 * Visual gluing — same semantics as `ElevationProfileWeb` so the histogram
 * can sit immediately under a profile and feel like one continuous card.
 */
function classesForPosition(position) {
  switch (position) {
    case "top":
      return "rounded-t-xl border-b-0";
    case "middle":
      return "rounded-none border-y-0";
    case "bottom":
      return "rounded-b-xl border-t-0";
    default:
      return "rounded-xl";
  }
}

function paddingForPosition(position) {
  switch (position) {
    case "top":
      return "px-4 pt-4 pb-2";
    case "middle":
      return "px-4 py-2";
    case "bottom":
      return "px-4 pt-2 pb-3";
    default:
      return "p-4";
  }
}

export default function ElevationHistogramWeb({
  coordinates,
  height = 130,
  position,
  /**
   * Altitude (in metres) currently hovered on the elevation profile. When
   * provided, the matching bin is rendered with the "active" highlight, so
   * users can see which slice of the route corresponds to the point under
   * their cursor on the profile chart.
   */
  activeAltitude = null,
}) {
  const { t } = useTranslation("tools");
  const [hover, setHover] = useState(null);
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);
  const cardCorners = classesForPosition(position);
  const cardPadding = paddingForPosition(position);

  const hasAltitude = useMemo(() => {
    if (!coordinates || coordinates.length < 2) return false;
    return coordinates.some((c) => Number.isFinite(c.alt) && c.alt !== 0);
  }, [coordinates]);

  const hist = useMemo(() => buildHistogram(coordinates), [coordinates]);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (!hasAltitude || hist.bins.length === 0) {
    return (
      <div
        className={`bg-card/80 backdrop-blur-sm border border-border ${cardCorners} ${cardPadding} text-center text-sm text-muted-foreground`}
        data-testid="elevation-histogram-empty"
      >
        <BarChart3 className="h-5 w-5 mx-auto mb-2 text-muted-foreground/60" />
        {t("public_route.chart_no_alt_histogram", "Sem dados de altitude para o histograma.")}
      </div>
    );
  }

  // b99.9.91 — `padH` bumped 28 → 40 to fit Y-axis labels like
  // "75 km" or "100 km" without clipping the leading digit.
  const padH = 40;
  const padV = 16;
  const labelH = 18;
  const chartW = Math.max(0, width - padH * 2);
  const chartH = Math.max(0, height - padV * 2 - labelH);

  const maxKm = Math.max(...hist.bins.map((b) => b.km), 0.001);
  const nBins = hist.bins.length;
  const gap = 2;
  const barW = nBins > 0 ? Math.max(2, (chartW - gap * (nBins - 1)) / nBins) : 0;

  const xFor = (i) => padH + i * (barW + gap);
  const yFor = (km) => padV + chartH - (km / maxKm) * chartH;

  // Choose up to 5 evenly-spaced bin labels to avoid clutter.
  const labelStep = Math.max(1, Math.ceil(nBins / 5));

  const onMove = (evt) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const i = Math.min(
      nBins - 1,
      Math.max(0, Math.floor((x - padH) / (barW + gap)))
    );
    if (i < 0 || i >= nBins) {
      setHover(null);
      return;
    }
    setHover({ i });
  };

  const hovered = hover ? hist.bins[hover.i] : null;

  // External "active" bin — driven by `activeAltitude` from the profile's
  // hover. We compute this even when the user isn't hovering the histogram
  // itself so the two charts stay in sync. Local hover wins (manual takes
  // precedence) so the user can still inspect bins directly on the histogram.
  const activeBinIndex = useMemo(() => {
    if (hover) return hover.i;
    if (activeAltitude == null || !Number.isFinite(activeAltitude)) return null;
    if (hist.bins.length === 0 || !hist.binSize) return null;
    const lower = hist.bins[0].low;
    let idx = Math.floor((activeAltitude - lower) / hist.binSize);
    if (idx < 0) idx = 0;
    if (idx >= hist.bins.length) idx = hist.bins.length - 1;
    return idx;
  }, [hover, activeAltitude, hist.bins, hist.binSize]);

  return (
    <div
      className={`bg-card/80 backdrop-blur-sm border border-border ${cardCorners} ${cardPadding}`}
      data-testid="elevation-histogram"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-bold text-foreground">
            {t("public_route.chart_histogram_title", "Histograma de altitude")}
          </h3>
        </div>
        <span
          className="text-xs text-muted-foreground mr-7"
          data-testid="elevation-histogram-binsize"
        >
          {t("public_route.chart_binsize", "passo {{m}} m", { m: hist.binSize })}
        </span>
      </div>

      <div
        ref={containerRef}
        className="relative"
        onMouseLeave={() => setHover(null)}
      >
        {width > 0 && (
          <svg
            width={width}
            height={height}
            onMouseMove={onMove}
            style={{ display: "block" }}
            data-testid="elevation-histogram-chart"
          >
            {/* Baseline */}
            <line
              x1={padH}
              y1={padV + chartH}
              x2={padH + chartW}
              y2={padV + chartH}
              stroke="currentColor"
              className="text-border"
              strokeWidth={1}
            />

            {/* Bars */}
            {hist.bins.map((b, i) => {
              const h = (b.km / maxKm) * chartH;
              const x = xFor(i);
              const y = yFor(b.km);
              const active = activeBinIndex === i;
              return (
                <rect
                  key={i}
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(0, h)}
                  fill={
                    active
                      ? "rgb(234 88 12 / 0.85)"
                      : "rgb(234 88 12 / 0.55)"
                  }
                  stroke="rgb(234 88 12)"
                  strokeWidth={active ? 1 : 0.5}
                  data-testid={`elevation-histogram-bar-${i}`}
                />
              );
            })}

            {/* X-axis bin labels */}
            {hist.bins.map((b, i) => {
              if (i % labelStep !== 0 && i !== nBins - 1) return null;
              const x = xFor(i) + barW / 2;
              return (
                <text
                  key={`xl-${i}`}
                  x={x}
                  y={padV + chartH + 12}
                  textAnchor="middle"
                  className="fill-muted-foreground"
                  style={{ fontSize: 10 }}
                >
                  {b.low}
                </text>
              );
            })}

            {/* Y-axis max-km reference line */}
            <text
              x={padH - 4}
              y={padV + 8}
              textAnchor="end"
              className="fill-muted-foreground"
              style={{ fontSize: 10 }}
            >
              {maxKm >= 10 ? `${Math.round(maxKm)} km` : `${maxKm.toFixed(1)} km`}
            </text>
            <text
              x={padH - 4}
              y={padV + chartH}
              textAnchor="end"
              className="fill-muted-foreground"
              style={{ fontSize: 10 }}
            >
              0
            </text>
          </svg>
        )}

        {/* Tooltip */}
        {hovered && width > 0 && (
          <div
            className="absolute pointer-events-none bg-background/95 border border-border rounded-md px-2 py-1 text-xs shadow-md"
            style={{
              left: Math.min(
                width - 140,
                Math.max(0, xFor(hover.i) + barW / 2 - 70)
              ),
              top: 0,
              minWidth: 130,
            }}
            data-testid="elevation-histogram-tooltip"
          >
            <div className="flex items-center gap-1 text-foreground font-semibold">
              <Mountain className="h-3 w-3" />
              {hovered.low}–{hovered.high} m
            </div>
            <div className="text-muted-foreground">
              {hovered.km.toFixed(1)} km ·{" "}
              {hist.totalKm > 0
                ? ((hovered.km / hist.totalKm) * 100).toFixed(0)
                : 0}
              %
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
